import type { ReactNode } from 'react';

import { TorianFullLogo } from '@/components/icons/torian-full-logo';
import { AuthBackground } from '@/components/auth';
import { cn } from '@/lib/utils';

/**
 * Shared chrome for every Mini App screen.
 *
 * Renders the same AuthBackground used by the affiliate-torian-market
 * loading and sign-in screens, plus a Torian wordmark header and a
 * centered glassy card slot. Every route in this app should mount
 * inside this shell so the visual identity stays coherent.
 *
 * The body fades up via `.shell-fade-up` (respects `prefers-reduced-motion`).
 */
export function ScreenShell({
  eyebrow,
  title,
  description,
  children,
  footer,
  className,
  contentClassName,
}: {
  /** Tiny uppercase tag rendered above the title (e.g. "Bridge"). */
  eyebrow?: ReactNode;
  /** Hero title displayed on the screen header. */
  title?: ReactNode;
  /** Subtitle / description under the title. */
  description?: ReactNode;
  /** Main body of the screen — usually a single card. */
  children: ReactNode;
  /** Optional footer slot rendered below the main content (e.g. fine print). */
  footer?: ReactNode;
  className?: string;
  contentClassName?: string;
}) {
  return (
    <div
      className={cn(
        'bg-background relative isolate flex min-h-svh w-full flex-col overflow-hidden select-none',
        className,
      )}
    >
      <AuthBackground />

      <div className="relative flex min-h-svh w-full flex-col">
        <header className="relative flex items-center justify-between gap-3 px-4 pt-5 pb-3 sm:px-6 sm:pt-6">
          <TorianFullLogo className="text-foreground h-5 w-auto sm:h-6" />
          {eyebrow ? (
            <span className="text-primary inline-flex items-center gap-1.5 rounded-full bg-primary/12 px-2.5 py-1 text-[10px] font-semibold tracking-[0.18em] uppercase ring-1 ring-primary/25">
              <span className="auth-loading-dot bg-primary" />
              {eyebrow}
            </span>
          ) : null}
        </header>

        <main className="relative flex flex-1 flex-col items-center px-4 pb-8 sm:px-6">
          <div
            className={cn(
              'shell-fade-up flex w-full max-w-xl flex-col gap-5 pt-2 sm:gap-6 sm:pt-4',
              contentClassName,
            )}
          >
            {(title || description) && (
              <div className="flex flex-col gap-2 px-1">
                {title && (
                  <h1 className="text-foreground text-2xl font-semibold tracking-tight sm:text-3xl">
                    {title}
                  </h1>
                )}
                {description && (
                  <p className="text-muted-foreground text-sm leading-relaxed sm:text-[15px]">
                    {description}
                  </p>
                )}
              </div>
            )}
            {children}
          </div>
        </main>

        <footer className="text-muted-foreground relative flex flex-wrap items-center justify-between gap-3 px-4 pb-6 text-[11px] sm:px-6">
          {footer ?? (
            <>
              <span className="tabular-nums">
                © {new Date().getFullYear()} Torian Market
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="auth-loading-dot bg-primary" />
                Telegram Mini App
              </span>
            </>
          )}
        </footer>
      </div>
    </div>
  );
}
